import React, {
  createRef,
  useEffect,
  useRef,
  useState,
} from 'react';

import styles from './AIParaplanner.module.css';

// Slash commands and corresponding block types
const defaultCommandList = [
  { command: '/chart', label: 'Insert ChartBlock' },
  { command: '/table', label: 'Insert TableBlock' },
  { command: '/goal', label: 'Insert GoalBlock' },
  { command: '/summary', label: 'Insert AIResponseBlock' },
  { command: '/market-data', label: 'Insert MarketDataBlock' },
  { command: '/client-data', label: 'Insert ClientDataBlock' },
  { command: '/simulate', label: 'Insert ScenarioSimulationBlock' },
  { command: '/rewrite', label: 'Rewrite TextBlock' },
  { command: '/explain', label: 'Explain TextBlock' },
];

// Generate unique IDs for blocks
const generateId = () => '_' + Math.random().toString(36).substr(2, 9);

export default function AIParaplanner() {
  const [blocks, setBlocks] = useState([
    { id: generateId(), type: 'TextBlock', content: '' }
  ]);
  const [commandMenu, setCommandMenu] = useState({ open: false, position: { x: 0, y: 0 }, query: '' });
  const [selectedMenuItem, setSelectedMenuItem] = useState(0);
  const [history, setHistory] = useState({
    past: [],
    present: [{ id: generateId(), type: 'TextBlock', content: '' }],
    future: []
  });
  const containerRef = useRef(null);
  const blockRefs = useRef({});

  // Handle undo/redo
  const undo = () => {
    if (history.past.length > 0) {
      const newPast = history.past.slice(0, -1);
      const newPresent = history.past[history.past.length - 1];
      const newFuture = [history.present, ...history.future];
      
      setHistory({
        past: newPast,
        present: newPresent,
        future: newFuture
      });
      setBlocks(newPresent);
    }
  };

  const redo = () => {
    if (history.future.length > 0) {
      const newPast = [...history.past, history.present];
      const newPresent = history.future[0];
      const newFuture = history.future.slice(1);
      
      setHistory({
        past: newPast,
        present: newPresent,
        future: newFuture
      });
      setBlocks(newPresent);
    }
  };

  // Update history when blocks change
  useEffect(() => {
    if (blocks !== history.present) {
      setHistory({
        past: [...history.past, history.present],
        present: blocks,
        future: []
      });
    }
  }, [blocks]);

  // Handle container click to create/focus text block
  const handleContainerClick = (e) => {
    if (e.target === containerRef.current) {
      // Find the last text block or create a new one
      const lastTextBlock = [...blocks].reverse().find(b => b.type === 'TextBlock');
      if (lastTextBlock) {
        const ref = blockRefs.current[lastTextBlock.id];
        if (ref && ref.current) {
          ref.current.focus();
          // Move cursor to end of block
          const range = document.createRange();
          const sel = window.getSelection();
          range.selectNodeContents(ref.current);
          range.collapse(false);
          sel.removeAllRanges();
          sel.addRange(range);
        }
      } else {
        // Create new text block if none exists
        const newBlock = { id: generateId(), type: 'TextBlock', content: '' };
        setBlocks([...blocks, newBlock]);
        // Focus the new block after state update
        setTimeout(() => {
          const ref = blockRefs.current[newBlock.id];
          if (ref && ref.current) {
            ref.current.focus();
          }
        }, 0);
      }
    }
  };

  // Handle block operations
  const handleBlockKeyDown = (e, blockId) => {
    // Handle special slash menu navigation first
    if (commandMenu.open) {
      const filtered = defaultCommandList.filter(c => 
        commandMenu.query === '' ? true : c.command.toLowerCase().includes(commandMenu.query.toLowerCase()) ||
        c.label.toLowerCase().includes(commandMenu.query.toLowerCase())
      );
      
      if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
        e.preventDefault();
        e.stopPropagation();
        
        if (filtered.length > 0) {
          if (e.key === 'ArrowDown') {
            setSelectedMenuItem(prev => (prev + 1) % filtered.length);
          } else {
            setSelectedMenuItem(prev => prev <= 0 ? filtered.length - 1 : prev - 1);
          }
        }
        return;
      } 
      else if (e.key === 'Enter') {
        e.preventDefault();
        e.stopPropagation();
        
        if (filtered.length > 0 && selectedMenuItem >= 0 && selectedMenuItem < filtered.length) {
          handleCommandSelect(filtered[selectedMenuItem].command);
        }
        return;
      }
      else if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        setCommandMenu({ ...commandMenu, open: false });
        setSelectedMenuItem(0);
        return;
      }
    }

    // Handle other block operations
    if (e.key === 'Enter' && !e.shiftKey && !commandMenu.open) {
      e.preventDefault();
      // Get current block index
      let currentIndex = blocks.findIndex(b => b.id === blockId);
      
      // If we couldn't find a valid index, default to the end of the document
      if (currentIndex === -1) {
        currentIndex = blocks.length - 1;
      }
      
      // Create new empty block
      const newBlock = { id: generateId(), type: 'TextBlock', content: '' };
      const newBlocks = [...blocks];
      newBlocks.splice(currentIndex + 1, 0, newBlock);
      setBlocks(newBlocks);
      
      // Focus the new block after state update
      setTimeout(() => {
        const ref = blockRefs.current[newBlock.id];
        if (ref?.current) {
          ref.current.focus();
        }
      }, 0);
    } else if (e.key === 'ArrowUp' && !commandMenu.open) {
      e.preventDefault();
      const currentIndex = blocks.findIndex(b => b.id === blockId);
      if (currentIndex > 0) {
        const prevBlock = blocks[currentIndex - 1];
        if (prevBlock.type === 'TextBlock') {
          const ref = blockRefs.current[prevBlock.id];
          if (ref?.current) {
            ref.current.focus();
            // Move cursor to end of previous block
            const range = document.createRange();
            const sel = window.getSelection();
            range.selectNodeContents(ref.current);
            range.collapse(false);
            sel.removeAllRanges();
            sel.addRange(range);
          }
        } else {
          // Focus non-text block
          const blockElement = document.querySelector(`[data-block-id="${prevBlock.id}"]`);
          if (blockElement) {
            blockElement.focus();
          }
        }
      }
    } else if (e.key === 'ArrowDown' && !commandMenu.open) {
      e.preventDefault();
      const currentIndex = blocks.findIndex(b => b.id === blockId);
      if (currentIndex < blocks.length - 1) {
        const nextBlock = blocks[currentIndex + 1];
        if (nextBlock.type === 'TextBlock') {
          const ref = blockRefs.current[nextBlock.id];
          if (ref?.current) {
            ref.current.focus();
            // Move cursor to start of next block
            const range = document.createRange();
            const sel = window.getSelection();
            range.selectNodeContents(ref.current);
            range.collapse(true);
            sel.removeAllRanges();
            sel.addRange(range);
          }
        } else {
          // Focus non-text block
          const blockElement = document.querySelector(`[data-block-id="${nextBlock.id}"]`);
          if (blockElement) {
            blockElement.focus();
          }
        }
      }
    } else if (e.key === 'Backspace') {
      console.log('Backspace');
      const currentBlock = blocks.find(b => b.id === blockId);
      const currentIndex = blocks.findIndex(b => b.id === blockId);
      
      // For empty text blocks, directly use Delete logic
      if (currentBlock.type === 'TextBlock' && (!e.target.textContent || e.target.textContent.trim() === '')) {
        // Execute the exact same code as the Delete key handler
        e.preventDefault();
        const newBlocks = blocks.filter((_, i) => i !== currentIndex);
        console.log('newBlocks', newBlocks);
        setBlocks(newBlocks);
        
        // Focus the previous block or next block
        setTimeout(() => {
          const targetIndex = currentIndex > 0 ? currentIndex - 1 : 0;
          if (newBlocks.length > 0) {
            const targetBlock = newBlocks[targetIndex];
            if (targetBlock.type === 'TextBlock') {
              const ref = blockRefs.current[targetBlock.id];
              if (ref?.current) {
                ref.current.focus();
                // Move cursor to end of target block
                const range = document.createRange();
                const sel = window.getSelection();
                range.selectNodeContents(ref.current);
                range.collapse(false);
                sel.removeAllRanges();
                sel.addRange(range);
              }
            } else {
              // Focus non-text block
              const blockElement = document.querySelector(`[data-block-id="${targetBlock.id}"]`);
              if (blockElement) {
                blockElement.focus();
              }
            }
          }
        }, 0);
        return;
      }
      
      // For non-text blocks with double backspace
      if (currentBlock.type !== 'TextBlock') {
        const now = Date.now();
        const lastBackspace = parseInt(e.target.dataset.lastBackspace || 0, 10);
        const timeDiff = now - lastBackspace;
        
        if (lastBackspace && timeDiff < 500) {
          // Execute the exact same code as the Delete key handler
          e.preventDefault();
          const newBlocks = blocks.filter((_, i) => i !== currentIndex);
          console.log('newBlocks', newBlocks);
          setBlocks(newBlocks);
          
          // Focus the previous block or next block
          setTimeout(() => {
            const targetIndex = currentIndex > 0 ? currentIndex - 1 : 0;
            if (newBlocks.length > 0) {
              const targetBlock = newBlocks[targetIndex];
              if (targetBlock.type === 'TextBlock') {
                const ref = blockRefs.current[targetBlock.id];
                if (ref?.current) {
                  ref.current.focus();
                  // Move cursor to end of target block
                  const range = document.createRange();
                  const sel = window.getSelection();
                  range.selectNodeContents(ref.current);
                  range.collapse(false);
                  sel.removeAllRanges();
                  sel.addRange(range);
                }
              } else {
                // Focus non-text block
                const blockElement = document.querySelector(`[data-block-id="${targetBlock.id}"]`);
                if (blockElement) {
                  blockElement.focus();
                }
              }
            }
          }, 0);
          return;
        } else {
          // Record this backspace press
          e.target.dataset.lastBackspace = now;
        }
      }
    } else if (e.key === 'Delete') {
      e.preventDefault();
      const currentIndex = blocks.findIndex(b => b.id === blockId);
      if (currentIndex > 0 || blocks.length > 1) {
        const newBlocks = blocks.filter((_, i) => i !== currentIndex);
        setBlocks(newBlocks);
        
        // Focus the previous block or next block
        setTimeout(() => {
          const targetIndex = currentIndex > 0 ? currentIndex - 1 : 0;
          if (newBlocks.length > 0) {
            const targetBlock = newBlocks[targetIndex];
            if (targetBlock.type === 'TextBlock') {
              const ref = blockRefs.current[targetBlock.id];
              if (ref?.current) {
                ref.current.focus();
                // Move cursor to end of target block
                const range = document.createRange();
                const sel = window.getSelection();
                range.selectNodeContents(ref.current);
                range.collapse(false);
                sel.removeAllRanges();
                sel.addRange(range);
              }
            } else {
              // Focus non-text block
              const blockElement = document.querySelector(`[data-block-id="${targetBlock.id}"]`);
              if (blockElement) {
                blockElement.focus();
              }
            }
          }
        }, 0);
      }
    } else if (e.key === '/' && e.target.dataset.blockType === 'TextBlock' && !commandMenu.open) {
      // Handle slash commands
      const selection = window.getSelection();
      const range = selection.getRangeAt(0);
      const rect = range.getBoundingClientRect();
      
      // Calculate optimal menu position
      const viewportHeight = window.innerHeight;
      const spaceBelow = viewportHeight - rect.bottom;
      const spaceAbove = rect.top;
      const position = { x: rect.left, y: 0 };
      
      // Check if more space below or above
      if (spaceBelow < 300 && spaceAbove > spaceBelow) {
        // Position above the element if not enough space below
        position.y = rect.top - 10;
        position.direction = 'up';
      } else {
        // Default: position below
        position.y = rect.bottom;
        position.direction = 'down';
      }
      
      setCommandMenu({ open: true, position, query: '' });
      setSelectedMenuItem(0);
    }
    
    // Handle undo/redo
    if (e.ctrlKey || e.metaKey) {
      if (e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        undo();
      } else if ((e.key === 'z' && e.shiftKey) || e.key === 'y') {
        e.preventDefault();
        redo();
      }
    }
  };

  // Handle global keydown for command menu
  const handleKeyDown = (e) => {
    if (commandMenu.open) {
      if (e.key === 'Escape') {
        setCommandMenu({ ...commandMenu, open: false });
        setSelectedMenuItem(0);
      }
    }
  };

  useEffect(() => {
    const el = containerRef.current;
    el.addEventListener('keydown', handleKeyDown);
    return () => el.removeEventListener('keydown', handleKeyDown);
  }, [commandMenu]);

  // Insert a new block based on selected command
  const handleCommandSelect = (command) => {
    let newType = 'TextBlock';
    switch (command) {
      case '/chart': newType = 'ChartBlock'; break;
      case '/table': newType = 'TableBlock'; break;
      case '/goal': newType = 'GoalBlock'; break;
      case '/summary': newType = 'AIResponseBlock'; break;
      case '/market-data': newType = 'MarketDataBlock'; break;
      case '/client-data': newType = 'ClientDataBlock'; break;
      case '/simulate': newType = 'ScenarioSimulationBlock'; break;
      case '/rewrite': newType = 'TextBlock'; break;
      case '/explain': newType = 'TextBlock'; break;
    }

    const activeElement = document.activeElement;
    let currentBlockIndex = -1;
    
    // Get the current block index more reliably
    if (activeElement && activeElement.dataset && activeElement.dataset.blockId) {
      currentBlockIndex = blocks.findIndex(b => b.id === activeElement.dataset.blockId);
    }
    
    // If we couldn't find a valid index, default to the end of the document
    if (currentBlockIndex === -1) {
      currentBlockIndex = blocks.length - 1;
    }
    
    const newBlock = { id: generateId(), type: newType, content: '' };
    
    // Insert new block after current block
    const newBlocks = [...blocks];
    newBlocks.splice(currentBlockIndex + 1, 0, newBlock);
    
    // Automatically add a text block after non-text blocks
    if (newType !== 'TextBlock') {
      const textBlock = { id: generateId(), type: 'TextBlock', content: '' };
      newBlocks.splice(currentBlockIndex + 2, 0, textBlock);
    }
    
    setBlocks(newBlocks);
    setCommandMenu({ ...commandMenu, open: false });
    setSelectedMenuItem(0);

    // Focus the new block if it's text, otherwise focus the following text block
    setTimeout(() => {
      if (newType === 'TextBlock') {
        const ref = blockRefs.current[newBlock.id];
        if (ref && ref.current) {
          ref.current.focus();
          // Move cursor to start of new block
          const range = document.createRange();
          const sel = window.getSelection();
          range.selectNodeContents(ref.current);
          range.collapse(true);
          sel.removeAllRanges();
          sel.addRange(range);
        }
      } else {
        // Focus the text block that follows the non-text block
        const nextBlockIndex = currentBlockIndex + 2;
        if (nextBlockIndex < newBlocks.length) {
          const textBlockId = newBlocks[nextBlockIndex].id;
          const ref = blockRefs.current[textBlockId];
          if (ref && ref.current) {
            ref.current.focus();
            // Move cursor to start of new block
            const range = document.createRange();
            const sel = window.getSelection();
            range.selectNodeContents(ref.current);
            range.collapse(true);
            sel.removeAllRanges();
            sel.addRange(range);
          }
        }
      }
    }, 0);
  };

  // Update block content or config
  const updateBlock = (id, updates) => {
    setBlocks(blocks.map(b => b.id === id ? { ...b, ...updates } : b));
  };

  return (
    <div
      ref={containerRef}
      className={styles.aiParaplanner}
      tabIndex={0}
      onClick={handleContainerClick}
    >
      {blocks.map(block => (
        <Block
          key={block.id}
          block={block}
          ref={(el) => {
            if (block.type === 'TextBlock') {
              blockRefs.current[block.id] = blockRefs.current[block.id] || createRef();
              blockRefs.current[block.id].current = el;
            }
          }}
          onUpdate={updates => updateBlock(block.id, updates)}
          onKeyDown={handleBlockKeyDown}
          commandMenu={commandMenu}
          setCommandMenu={setCommandMenu}
          blocks={blocks}
          setBlocks={setBlocks}
          blockRefs={blockRefs}
          generateId={generateId}
        />
      ))}

      {commandMenu.open && (
        <SlashCommandMenu
          position={commandMenu.position}
          query={commandMenu.query}
          onSelect={handleCommandSelect}
          selectedIndex={selectedMenuItem}
          onQueryChange={(newQuery) => {
            // Update only the query part, keep position and open state
            setCommandMenu({...commandMenu, query: newQuery});
          }}
        />
      )}
    </div>
  );
}

// ForwardRef to allow focusing
const Block = React.forwardRef(({ 
  block, 
  onUpdate, 
  onKeyDown, 
  commandMenu, 
  setCommandMenu, 
  blocks, 
  setBlocks, 
  blockRefs,
  generateId
}, ref) => {
  const [isFocused, setIsFocused] = useState(false);
  const [showSlashHighlight, setShowSlashHighlight] = useState(false);
  const blockRef = useRef(null);
  const nonTextBlockRef = useRef(null);

  const handleInput = (e) => {
    const content = e.currentTarget.textContent || '';
    onUpdate({ content });
    
    // Check if content starts with slash
    if (content.startsWith('/')) {
      setShowSlashHighlight(true);
      
      // Get position of the slash character
      const textNode = e.currentTarget.firstChild;
      const range = document.createRange();
      
      if (textNode) {
        // Position at the slash character
        range.setStart(textNode, 0);
        range.setEnd(textNode, 1);
      } else {
        // Fallback if no text node available
        range.selectNodeContents(e.currentTarget);
        range.collapse(true);
      }
      
      const rect = range.getBoundingClientRect();
      
      // Calculate optimal menu position
      const viewportHeight = window.innerHeight;
      const spaceBelow = viewportHeight - rect.bottom;
      const spaceAbove = rect.top;
      const position = { x: rect.left, y: 0 };
      
      // Check if more space below or above
      if (spaceBelow < 300 && spaceAbove > spaceBelow) {
        // Position above the element if not enough space below
        position.y = rect.top - 10;
        position.direction = 'up';
      } else {
        // Default: position below
        position.y = rect.bottom;
        position.direction = 'down';
      }
      
      // Always update the command menu with the current query
      setCommandMenu({ 
        open: true, 
        position, 
        query: content.slice(1) 
      });
    } else {
      // Close menu if content doesn't start with slash
      setShowSlashHighlight(false);
      if (commandMenu.open) {
        setCommandMenu({...commandMenu, open: false});
      }
    }
  };

  const handleBlockKeyDown = (e) => {
    onKeyDown(e, block.id);
    
    // Handle keyboard navigation in menu without closing it
    if (commandMenu.open && block.content && block.content.startsWith('/')) {
      if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
        e.preventDefault();
        e.stopPropagation();
      }
    }
  };

  const handleFocus = (e) => {
    setIsFocused(true);
    
    // Check if content starts with slash to show highlight and reopen menu
    if (block.type === 'TextBlock' && block.content && block.content.startsWith('/')) {
      setShowSlashHighlight(true);
      
      // Get the position of the slash character
      const selection = window.getSelection();
      const textNode = selection.anchorNode;
      let range;
      
      if (textNode && textNode.nodeType === Node.TEXT_NODE) {
        range = document.createRange();
        range.setStart(textNode, 0);
        range.setEnd(textNode, 1);
      } else {
        // Fallback to element positioning
        const el = document.querySelector(`[data-block-id="${block.id}"]`);
        if (el) {
          range = document.createRange();
          range.selectNodeContents(el);
          range.collapse(true);
        }
      }
      
      if (range) {
        const rect = range.getBoundingClientRect();
        
        // Calculate optimal menu position
        const viewportHeight = window.innerHeight;
        const spaceBelow = viewportHeight - rect.bottom;
        const spaceAbove = rect.top;
        const position = { x: rect.left, y: 0 };
        
        // Check if more space below or above
        if (spaceBelow < 300 && spaceAbove > spaceBelow) {
          position.y = rect.top - 10;
          position.direction = 'up';
        } else {
          position.y = rect.bottom;
          position.direction = 'down';
        }
        
        setCommandMenu({ open: true, position, query: block.content.slice(1) });
      }
    }
  };

  const handleBlur = (e) => {
    setIsFocused(false);
    
    // Don't hide slash highlight immediately - this helps with menu interaction
    // Only close command menu if we're not clicking on it
    const relatedTarget = e.relatedTarget;
    if (!relatedTarget || !relatedTarget.closest(`.${styles.slashMenu}`)) {
      setTimeout(() => {
        setShowSlashHighlight(false);
        setCommandMenu(prev => ({...prev, open: false}));
      }, 150);
    }
  };

  // Common handler for both text and non-text blocks
  const handleBlockClick = (e) => {
    if (e.target === blockRef.current || e.currentTarget === blockRef.current) {
      setIsFocused(true);
    }
  };

  // Create a new block after this one when double clicking on a non-text block
  const handleBlockDoubleClick = (e) => {
    if (block.type !== 'TextBlock') {
      // Create a new text block after this one
      const newBlock = { id: generateId(), type: 'TextBlock', content: '' };
      const currentIndex = blocks.findIndex(b => b.id === block.id);
      
      if (currentIndex !== -1) {
        const newBlocks = [...blocks];
        newBlocks.splice(currentIndex + 1, 0, newBlock);
        setBlocks(newBlocks);
        
        // Focus the new block after state update
        setTimeout(() => {
          const ref = blockRefs.current[newBlock.id];
          if (ref && ref.current) {
            ref.current.focus();
          }
        }, 0);
      }
    }
  };

  if (block.type === 'TextBlock') {
    return (
      <div className={`${styles.blockWrapper}`}>
        <div className={styles.blockActions}>
          <button 
            className={styles.blockActionButton}
            title="Delete block"
            onClick={(e) => {
              e.stopPropagation();
              const currentIndex = blocks.findIndex(b => b.id === block.id);
              if (currentIndex !== -1) {
                const newBlocks = blocks.filter((_, i) => i !== currentIndex);
                setBlocks(newBlocks);
                
                // Focus the previous block or next block
                setTimeout(() => {
                  const targetIndex = currentIndex > 0 ? currentIndex - 1 : 0;
                  if (newBlocks.length > 0) {
                    const targetBlock = newBlocks[targetIndex];
                    const targetRef = blockRefs.current[targetBlock.id];
                    if (targetBlock.type === 'TextBlock' && targetRef && targetRef.current) {
                      targetRef.current.focus();
                    }
                  }
                }, 0);
              }
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" fill="currentColor"/>
            </svg>
          </button>
          <button
            className={styles.blockActionButton}
            title="Add text block"
            onClick={(e) => {
              e.stopPropagation();
              // Create a new text block after this one
              const newBlock = { id: generateId(), type: 'TextBlock', content: '' };
              const currentIndex = blocks.findIndex(b => b.id === block.id);
              
              if (currentIndex !== -1) {
                const newBlocks = [...blocks];
                newBlocks.splice(currentIndex + 1, 0, newBlock);
                setBlocks(newBlocks);
                
                // Focus the new block after state update
                setTimeout(() => {
                  const ref = blockRefs.current[newBlock.id];
                  if (ref && ref.current) {
                    ref.current.focus();
                  }
                }, 0);
              }
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" fill="currentColor"/>
            </svg>
          </button>
        </div>
        <div
          contentEditable
          suppressContentEditableWarning
          onInput={handleInput}
          onKeyDown={handleBlockKeyDown}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onClick={handleBlockClick}
          data-block-type={block.type}
          data-block-id={block.id}
          className={`${styles.block} ${styles[block.type.toLowerCase()]} ${showSlashHighlight ? styles.slashHighlight : ''}`}
          ref={(el) => {
            blockRef.current = el;
            if (typeof ref === 'function') ref(el);
            else if (ref) ref.current = el;
          }}
          style={{ outline: 'none' }}
        >
          {(isFocused && !block.content) && (
            <span className={styles.placeholder}>Type '/' for commands</span>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={`${styles.blockWrapper}`}>
      <div className={styles.blockActions}>
        <button 
          className={styles.blockActionButton}
          title="Delete block"
          onClick={(e) => {
            e.stopPropagation();
            const currentIndex = blocks.findIndex(b => b.id === block.id);
            if (currentIndex !== -1) {
              const newBlocks = blocks.filter((_, i) => i !== currentIndex);
              setBlocks(newBlocks);
              
              // Focus the previous block or next block
              setTimeout(() => {
                const targetIndex = currentIndex > 0 ? currentIndex - 1 : 0;
                if (newBlocks.length > 0) {
                  const targetBlock = newBlocks[targetIndex];
                  const targetRef = blockRefs.current[targetBlock.id];
                  if (targetBlock.type === 'TextBlock' && targetRef && targetRef.current) {
                    targetRef.current.focus();
                  } else {
                    const blockElement = document.querySelector(`[data-block-id="${targetBlock.id}"]`);
                    if (blockElement) {
                      blockElement.focus();
                    }
                  }
                }
              }, 0);
            }
          }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" fill="currentColor"/>
          </svg>
        </button>
        <button
          className={styles.blockActionButton}
          title="Add text block"
          onClick={(e) => {
            e.stopPropagation();
            // Create a new text block after this one
            const newBlock = { id: generateId(), type: 'TextBlock', content: '' };
            const currentIndex = blocks.findIndex(b => b.id === block.id);
            
            if (currentIndex !== -1) {
              const newBlocks = [...blocks];
              newBlocks.splice(currentIndex + 1, 0, newBlock);
              setBlocks(newBlocks);
              
              // Focus the new block after state update
              setTimeout(() => {
                const ref = blockRefs.current[newBlock.id];
                if (ref && ref.current) {
                  ref.current.focus();
                }
              }, 0);
            }
          }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" fill="currentColor"/>
          </svg>
        </button>
      </div>
      <div 
        className={`${styles.block} ${styles[block.type.toLowerCase()]} ${isFocused ? styles.blockFocused : ''}`}
        data-block-type={block.type}
        data-block-id={block.id}
        ref={(el) => {
          nonTextBlockRef.current = el;
          blockRef.current = el;
        }}
        onClick={handleBlockClick}
        onDoubleClick={handleBlockDoubleClick}
        onKeyDown={handleBlockKeyDown}
        onFocus={handleFocus}
        onBlur={handleBlur}
        tabIndex={0}
      >
        <div className={styles.blockContent}>
          {block.type}
        </div>
      </div>
    </div>
  );
});

const SlashCommandMenu = React.forwardRef(({ position, query, onSelect, selectedIndex, onQueryChange }, ref) => {
  const [searchQuery, setSearchQuery] = useState(query);
  const inputRef = useRef(null);
  const menuRef = useRef(null);
  
  // Focus the search input when the component mounts
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);
  
  // Update local search when external query changes
  useEffect(() => {
    setSearchQuery(query);
  }, [query]);
  
  const handleSearchChange = (e) => {
    const newQuery = e.target.value;
    setSearchQuery(newQuery);
    if (onQueryChange) {
      onQueryChange(newQuery);
    }
  };
  
  const handleMouseDown = (e) => {
    // Prevent blur on the parent element
    e.preventDefault();
  };
  
  const filtered = defaultCommandList.filter(c => 
    searchQuery === '' ? true : c.command.toLowerCase().includes(searchQuery.toLowerCase()) ||
    c.label.toLowerCase().includes(searchQuery.toLowerCase())
  );
  
  const menuStyle = {
    position: 'absolute',
    left: position.x,
    top: position.direction === 'up' ? 'auto' : position.y,
    bottom: position.direction === 'up' ? `calc(100vh - ${position.y}px)` : 'auto',
    maxHeight: '300px',
    overflow: 'auto',
    zIndex: 1000,
    width: '250px'
  };
  
  return (
    <div 
      className={styles.slashMenu} 
      style={menuStyle} 
      ref={(el) => {
        menuRef.current = el;
        if (typeof ref === 'function') ref(el);
        else if (ref) ref.current = el;
      }}
      onMouseDown={handleMouseDown}
    >
      <div className={styles.slashMenuHeader}>
        <span>Commands</span>
        <span className={styles.escHint}>ESC to close</span>
      </div>
      <div className={styles.slashMenuSearch}>
        <input
          ref={inputRef}
          type="text"
          value={searchQuery}
          onChange={handleSearchChange}
          placeholder="Search commands..."
          className={styles.slashMenuSearchInput}
          onMouseDown={handleMouseDown}
        />
      </div>
      <ul className={styles.slashMenuList}>
        {filtered.map((cmd, index) => (
          <li 
            key={cmd.command} 
            className={`${styles.slashMenuItem} ${index === selectedIndex ? styles.slashMenuItemSelected : ''}`} 
            onMouseDown={(e) => {
              e.preventDefault();
              onSelect(cmd.command);
            }}
          >
            <strong>{cmd.command}</strong> - {cmd.label}
          </li>
        ))}
        {filtered.length === 0 && (
          <li className={styles.slashMenuNoResults}>No commands found</li>
        )}
      </ul>
    </div>
  );
});
